import * as Sentry from '@sentry/react-native';
import { confirmResetPassword, confirmSignIn, confirmSignUp, resendSignUpCode, resetPassword, signIn, SignInOutput, signOut, signUp } from 'aws-amplify/auth';
import * as Clipboard from 'expo-clipboard';
import * as React from 'react';
import { ActivityIndicator, Platform, Text, TextInput, TouchableOpacity, View } from 'react-native';
import QRCode from 'react-native-qrcode-svg';

import { HelloWave } from '@/components/HelloWave';
import PhoneNumberInput from '@/components/PhoneNumberInput';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { isReviewer } from '@/config/reviewers';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useCampaignStore } from '@/stores/campaignStore';
import { useLeafletingStore } from '@/stores/leafletingStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { switchConfiguration } from '@/utils/configManager';
import { getEmailErrorMessage, isValidEmail, normalizeEmail } from '@/utils/emailValidation';
import { getPhoneNumberErrorMessage, isValidPhoneNumber, normalizePhoneNumber } from '@/utils/phoneValidation';

import AppStyles from '../constants/Styles';
import { useAuth } from '../hooks/useAutoLogin';

enum MySignInStep {
  EMAIL = 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE',
  EMAIL_SETUP = 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP',
  SMS = 'CONFIRM_SIGN_IN_WITH_SMS_CODE',
  TOTP = 'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
  TOTP_SETUP = 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP',
  NEW_PASSWORD = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
  MFA_SELECTION = 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION',
  CONFIRM_SIGN_UP = 'CONFIRM_SIGN_UP',
  DONE = 'DONE',
}

export function SignInForm() {
  const { isLoading, setIsAuthenticated } = useAuth();
  const { rememberMe, setRememberMe, setRememberedUsername, clearRememberedUsername, rememberedUsername } = useNavigationStore();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [showCodeInput, setShowCodeInput] = React.useState(false);
  const [mfaType, setMfaType] = React.useState<'sms' | 'totp' | null>(null);
  const [showTotpSetup, setShowTotpSetup] = React.useState(false);
  const [totpSecret, setTotpSecret] = React.useState<string | null>(null);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [showMfaSelection, setShowMfaSelection] = React.useState(false);
  const [allowedMfaTypes, setAllowedMfaTypes] = React.useState<string[]>([]);

  // Registration state
  const [showSignUp, setShowSignUp] = React.useState(false);
  const [signUpPassword, setSignUpPassword] = React.useState('');
  const [signUpEmail, setSignUpEmail] = React.useState('');
  const [signUpName, setSignUpName] = React.useState('');
  const [signUpPhone, setSignUpPhone] = React.useState('');
  const [signUpCode, setSignUpCode] = React.useState('');
  const [awaitingConfirmation, setAwaitingConfirmation] = React.useState(false);

  // Forgotten password state
  const [showForgotPassword, setShowForgotPassword] = React.useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = React.useState('');
  const [forgotPasswordCode, setForgotPasswordCode] = React.useState('');
  const [newPasswordReset, setNewPasswordReset] = React.useState('');
  const [awaitingPasswordReset, setAwaitingPasswordReset] = React.useState(false);

  // Load remembered username on component mount and when it changes
  React.useEffect(() => {
    if (rememberedUsername) {
      setUsername(rememberedUsername);
    }
  }, [rememberedUsername]);

  // Generate otpauth URI for QR code
  const otpauthUri = React.useMemo(() => {
    if (!totpSecret || !username) return '';
    return `otpauth://totp/${encodeURIComponent(username)}?secret=${totpSecret}&issuer=VotesNow`;
  }, [totpSecret, username]);

  const handleSignIn = async () => {
    setLoading(true);
    setError(null);
    // Reset MFA type for new sign-in attempt
    setMfaType(null);
    
    // Validate email format
    if (!username.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    
    if (!isValidEmail(username)) {
      const emailError = getEmailErrorMessage(username);
      setError(emailError || 'Invalid email format');
      setLoading(false);
      return;
    }
    
    try {
      // Switch configuration BEFORE attempting sign in if user is a reviewer
      if (isReviewer(username)) {
        console.log('Switching configuration for reviewer before sign in:', username);
        await switchConfiguration(username);
      } else {
        // Ensure we're using default configuration for non-reviewers
        await switchConfiguration(null);
      }
      
      const user = await signIn({ 
        username: normalizeEmail(username), 
        password 
      });
      
      // Handle remember me functionality
      if (rememberMe) {
        setRememberedUsername(normalizeEmail(username));
      } else {
        clearRememberedUsername();
      }
      
      handleAnyNextStep(user);
    } catch (err: any) {
      console.error('[SignInForm] Sign in error:', err);
      setError(err.message || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAnyNextStep = (user: SignInOutput) => {
    if (user.isSignedIn) {
      setIsAuthenticated(true);
      // Reset MFA state when authentication is complete
      setMfaType(null);
      setShowCodeInput(false);
      setShowMfaSelection(false);
      setAllowedMfaTypes([]);
      
      // Set user context in Sentry for error tracking
      if (!__DEV__) {
        Sentry.setUser({
          id: normalizeEmail(username), // Use normalized email as user ID
          username: normalizeEmail(username),
        });
      }
    } else {
      switch (user.nextStep?.signInStep) {
        case MySignInStep.SMS:
          // Handle WhatsApp/SMS MFA
          setMfaType('sms');
          setShowCodeInput(true);
          break;
        case MySignInStep.TOTP:
          // Handle TOTP MFA (authenticator app)
          setMfaType('totp');
          setShowCodeInput(true);
          break;
        case MySignInStep.TOTP_SETUP:
          setTotpSecret(user.nextStep?.totpSetupDetails?.sharedSecret ?? null);
          setShowTotpSetup(true);
          break;
        case MySignInStep.NEW_PASSWORD:
          setShowNewPassword(true);
          break;
        case MySignInStep.MFA_SELECTION:
          // Handle MFA selection when user has multiple MFA methods
          setAllowedMfaTypes(user.nextStep?.allowedMFATypes || []);
          setShowMfaSelection(true);
          break;
        case MySignInStep.CONFIRM_SIGN_UP:
          // Handle sign-up confirmation during sign-in process
          setAwaitingConfirmation(true);
          setShowSignUp(true);
          // Set the email from the username that was used for sign-in
          setSignUpEmail(username);
          // Automatically request a new confirmation code
          handleAutoResendCode(username);
          break;
        default:
          // Log unexpected nextStep to Sentry for investigation
          console.log('Unexpected Cognito signInStep:', user.nextStep);
          Sentry.captureException(new Error('Unexpected Cognito signInStep'), {
            extra: {
              nextStep: user.nextStep,
              cognitoResponse: user,
            },
          });
          break;
      }
    }
  };

  const handleMfaSelection = async (selectedMfaType: 'SMS' | 'TOTP') => {
    setLoading(true);
    setError(null);
    try {
      // Respond to the MFA selection challenge
      const user = await confirmSignIn({ 
        challengeResponse: selectedMfaType 
      });
      setShowMfaSelection(false);
      handleAnyNextStep(user);
    } catch (err: any) {
      console.error('[SignInForm] MFA selection error:', err);
      setError(err.message || 'MFA selection failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await confirmSignIn({ challengeResponse: code });
      handleAnyNextStep(user);
    } catch (err: any) {
      console.error('[SignInForm] TFA confirmation error:', err);
      setError(err.message || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await confirmSignIn({ challengeResponse: newPassword });
      handleAnyNextStep(user);
    } catch (err: any) {
      console.error('[SignInForm] New password confirmation error:', err);
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  // Registration handlers
  const handleSignUp = async () => {
    setLoading(true);
    setError(null);
    
    // Validate required fields
    if (!signUpName.trim()) {
      setError('Full name is required');
      setLoading(false);
      return;
    }
    
    if (!signUpEmail.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    
    if (!isValidEmail(signUpEmail)) {
      const emailError = getEmailErrorMessage(signUpEmail);
      setError(emailError || 'Invalid email format');
      setLoading(false);
      return;
    }
    
    if (!signUpPhone.trim()) {
      setError('Phone number is required');
      setLoading(false);
      return;
    }
    
    if (!isValidPhoneNumber(signUpPhone)) {
      setError(getPhoneNumberErrorMessage(signUpPhone));
      setLoading(false);
      return;
    }
    
    if (!signUpPassword.trim()) {
      setError('Password is required');
      setLoading(false);
      return;
    }
    
    try {
      await signUp({
        username: normalizeEmail(signUpEmail),
        password: signUpPassword,
        options: {
          userAttributes: {
            name: signUpName.trim(),
            phone_number: normalizePhoneNumber(signUpPhone),
          },
        },
      });
      setAwaitingConfirmation(true);
    } catch (err: any) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async () => {
    setLoading(true);
    setError(null);
    try {
      await confirmSignUp({ username: normalizeEmail(signUpEmail), confirmationCode: signUpCode });
      
      // Handle remember me functionality for registration
      if (rememberMe) {
        setRememberedUsername(normalizeEmail(signUpEmail));
      } else {
        clearRememberedUsername();
      }
      
      setShowSignUp(false);
      setAwaitingConfirmation(false);
      setSignUpPassword('');
      setSignUpEmail('');
      setSignUpName('');
      setSignUpPhone('');
      setSignUpCode('');
      setError('Registration successful! Please sign in.');
    } catch (err: any) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError(null);
    try {
      await resendSignUpCode({ username: normalizeEmail(signUpEmail) });
      setError('Confirmation code resent.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoResendCode = async (email: string) => {
    try {
      await resendSignUpCode({ username: normalizeEmail(email) });
    } catch (err: any) {
      console.error('[SignInForm] Auto resend code error:', err);
      setError(err.message || 'Failed to send confirmation code');
    }
  };

  // Forgotten password handlers
  const handleForgotPassword = async () => {
    setLoading(true);
    setError(null);
    
    // Validate email format
    if (!forgotPasswordEmail.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    
    if (!isValidEmail(forgotPasswordEmail)) {
      const emailError = getEmailErrorMessage(forgotPasswordEmail);
      setError(emailError || 'Invalid email format');
      setLoading(false);
      return;
    }
    
    try {
      await resetPassword({ username: normalizeEmail(forgotPasswordEmail) });
      setAwaitingPasswordReset(true);
      setError('Password reset code sent to your email.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmResetPassword = async () => {
    setLoading(true);
    setError(null);
    try {
      await confirmResetPassword({
        username: normalizeEmail(forgotPasswordEmail),
        confirmationCode: forgotPasswordCode,
        newPassword: newPasswordReset
      });
      setShowForgotPassword(false);
      setAwaitingPasswordReset(false);
      setForgotPasswordEmail('');
      setForgotPasswordCode('');
      setNewPasswordReset('');
      setError('Password reset successful! Please sign in with your new password.');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const colorScheme = useColorScheme();
  const placeholderTextColor = colorScheme === 'dark' ? '#888' : '#A9A9A9';

  if (isLoading || loading) {
    return (
      <View style={AppStyles.centeredContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // Get appropriate title based on current screen
  const getTitle = () => {
    if (showSignUp) {
      return awaitingConfirmation ? 'Confirm your registration' : 'Create your account';
    } else if (showForgotPassword) {
      return awaitingPasswordReset ? 'Enter reset code and new password' : 'Reset your password';
    } else if (showNewPassword) {
      return 'Set a new password';
    } else if (showTotpSetup) {
      return 'Set up two-factor authentication';
    } else if (showCodeInput) {
      if (mfaType === 'sms') {
        return 'Enter the code from WhatsApp';
      } else if (mfaType === 'totp') {
        return 'Enter the code from your authenticator app';
      }
      return 'Enter verification code';
    } else {
      return 'Please sign in to continue';
    }
  };

  return (
    <ThemedView style={[AppStyles.centeredContainer, { alignItems: 'stretch' }]}>
      <HelloWave />
      <ThemedText style={AppStyles.titleText}>{getTitle()}</ThemedText>
      <View style={{ flex: 1, width: '100%', maxWidth: 300, alignSelf: 'center' }}>
        {showMfaSelection ? (
          <View>
            <ThemedText style={{ marginBottom: 12, textAlign: 'center' }}>
              Choose your preferred verification method:
            </ThemedText>
            {allowedMfaTypes.includes('SMS') && (
              <TouchableOpacity
                style={[AppStyles.primaryButton, { marginBottom: 8 }]}
                onPress={() => handleMfaSelection('SMS')}
                disabled={loading}
              >
                <Text style={AppStyles.primaryButtonText}>
                  {loading ? 'Loading...' : 'WhatsApp'}
                </Text>
              </TouchableOpacity>
            )}
            {allowedMfaTypes.includes('TOTP') && (
              <TouchableOpacity
                style={[AppStyles.primaryButton, { marginBottom: 8 }]}
                onPress={() => handleMfaSelection('TOTP')}
                disabled={loading}
              >
                <Text style={AppStyles.primaryButtonText}>
                  {loading ? 'Loading...' : 'Authenticator App'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        ) : showSignUp ? (
          <View>
            {!awaitingConfirmation ? (
              <>
                <TextInput
                  placeholder="Full Name"
                  value={signUpName}
                  onChangeText={setSignUpName}
                  autoCapitalize="words"
                  style={[AppStyles.textInput, { marginBottom: 12 }]}
                  placeholderTextColor={placeholderTextColor}
                />
                <TextInput
                  placeholder="Email"
                  value={signUpEmail}
                  onChangeText={setSignUpEmail}
                  autoCapitalize="none"
                  style={[AppStyles.textInput, { marginBottom: 12 }]}
                  placeholderTextColor={placeholderTextColor}
                />
                <PhoneNumberInput
                  value={signUpPhone}
                  onChangeText={setSignUpPhone}
                  placeholder="Phone Number"
                  placeholderTextColor={placeholderTextColor}
                />
                <TextInput
                  placeholder="Password"
                  value={signUpPassword}
                  onChangeText={setSignUpPassword}
                  secureTextEntry
                  style={[AppStyles.textInput, { marginBottom: 12 }]}
                  placeholderTextColor={placeholderTextColor}
                />
                {/* Remember Me Option - Web Only */}
                {Platform.OS === 'web' && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                    <TouchableOpacity
                      style={{
                        width: 20,
                        height: 20,
                        borderWidth: 2,
                        borderColor: rememberMe ? '#007AFF' : '#ccc',
                        backgroundColor: rememberMe ? '#007AFF' : 'transparent',
                        borderRadius: 3,
                        marginRight: 8,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                      onPress={() => setRememberMe(!rememberMe)}
                    >
                      {rememberMe && (
                        <Text style={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}>✓</Text>
                      )}
                    </TouchableOpacity>
                    <Text style={{ color: '#666', fontSize: 14 }}>Remember me</Text>
                  </View>
                )}
                <TouchableOpacity
                  style={[AppStyles.primaryButton, { marginBottom: 8 }]}
                  onPress={handleSignUp}
                >
                  <Text style={AppStyles.primaryButtonText}>Sign Up</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[AppStyles.secondaryButton, { marginBottom: 8 }]}
                  onPress={() => setShowSignUp(false)}
                >
                  <Text style={AppStyles.secondaryButtonText}>Back to Sign In</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <ThemedText>Please enter the code sent to the email address you entered.</ThemedText>
                <TextInput
                  placeholder="Confirmation Code"
                  value={signUpCode}
                  onChangeText={setSignUpCode}
                  keyboardType="number-pad"
                  style={[AppStyles.textInput, { marginBottom: 12 }]}
                  placeholderTextColor={placeholderTextColor}
                />
                <TouchableOpacity
                  style={[AppStyles.primaryButton, { marginBottom: 8 }]}
                  onPress={handleConfirmSignUp}
                >
                  <Text style={AppStyles.primaryButtonText}>Confirm Registration</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[AppStyles.secondaryButton, { marginBottom: 8 }]}
                  onPress={handleResendCode}
                >
                  <Text style={AppStyles.secondaryButtonText}>Resend Code</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[AppStyles.secondaryButton, { marginBottom: 8 }]}
                  onPress={() => setShowSignUp(false)}
                >
                  <Text style={AppStyles.secondaryButtonText}>Back to Sign In</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        ) : showNewPassword ? (
          <View>
            <ThemedText style={AppStyles.normalText}>Set a new password:</ThemedText>
            <TextInput
              placeholder="New password"
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry
              style={[AppStyles.textInput, { marginBottom: 12 }]}
              placeholderTextColor={placeholderTextColor}
            />
            <TouchableOpacity
              style={AppStyles.primaryButton}
              onPress={handleNewPassword}
            >
              <Text style={AppStyles.primaryButtonText}>Set New Password</Text>
            </TouchableOpacity>
          </View>
        ) : showTotpSetup && totpSecret ? (
          <View style={{ alignItems: 'center' }}>
            <ThemedText style={AppStyles.normalText}>Add this code to your authenticator app:</ThemedText>
            {Platform.OS === 'web' && otpauthUri ? <QRCode value={otpauthUri} size={180} /> : null}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 8 }}>
              <Text selectable style={{ fontWeight: 'bold', marginRight: 8 }}>{totpSecret}</Text>
              <TouchableOpacity
                onPress={async () => {
                  await Clipboard.setStringAsync(totpSecret);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                style={[AppStyles.linkButton, { paddingVertical: 2, paddingHorizontal: 8 }]}
              >
                <Text style={AppStyles.linkButtonText}>{copied ? 'Copied!' : 'Copy'}</Text>
              </TouchableOpacity>
            </View>
            <TextInput
              placeholder="Enter code from authenticator app"
              value={code}
              onChangeText={setCode}
              keyboardType="number-pad"
              style={[AppStyles.textInput, { marginBottom: 12 }]}
              placeholderTextColor={placeholderTextColor}
            />
            <TouchableOpacity
              style={AppStyles.primaryButton}
              onPress={handleConfirm}
            >
              <Text style={AppStyles.primaryButtonText}>Verify TOTP</Text>
            </TouchableOpacity>
          </View>
        ) : showCodeInput ? (
          <>
            <ThemedText style={{ marginBottom: 12, textAlign: 'center' }}>
              {mfaType === 'sms' 
                ? 'Enter the verification code sent to your WhatsApp number'
                : 'Enter the code from your authenticator app'
              }
            </ThemedText>
            <TextInput
              placeholder="Enter verification code"
              value={code}
              onChangeText={setCode}
              keyboardType="number-pad"
              style={[AppStyles.textInput, { marginBottom: 12 }]}
              placeholderTextColor={placeholderTextColor}
            />
            <TouchableOpacity
              style={AppStyles.primaryButton}
              onPress={handleConfirm}
            >
              <Text style={AppStyles.primaryButtonText}>Confirm Code</Text>
            </TouchableOpacity>
          </>
        ) : showForgotPassword ? (
          <View>
            {!awaitingPasswordReset ? (
              <>
                <TextInput
                  placeholder="Email"
                  value={forgotPasswordEmail}
                  onChangeText={setForgotPasswordEmail}
                  autoCapitalize="none"
                  style={[AppStyles.textInput, { marginBottom: 12 }]}
                  placeholderTextColor={placeholderTextColor}
                />
                <TouchableOpacity
                  style={[AppStyles.primaryButton, { marginBottom: 8 }]}
                  onPress={handleForgotPassword}
                >
                  <Text style={AppStyles.primaryButtonText}>Send Reset Code</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[AppStyles.secondaryButton, { marginBottom: 8 }]}
                  onPress={() => setShowForgotPassword(false)}
                >
                  <Text style={AppStyles.secondaryButtonText}>Back to Sign In</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <TextInput
                  placeholder="Reset Code"
                  value={forgotPasswordCode}
                  onChangeText={setForgotPasswordCode}
                  keyboardType="number-pad"
                  style={[AppStyles.textInput, { marginBottom: 12 }]}
                  placeholderTextColor={placeholderTextColor}
                />
                <TextInput
                  placeholder="New Password"
                  value={newPasswordReset}
                  onChangeText={setNewPasswordReset}
                  secureTextEntry
                  style={[AppStyles.textInput, { marginBottom: 12 }]}
                  placeholderTextColor={placeholderTextColor}
                />
                <TouchableOpacity
                  style={[AppStyles.primaryButton, { marginBottom: 8 }]}
                  onPress={handleConfirmResetPassword}
                >
                  <Text style={AppStyles.primaryButtonText}>Reset Password</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[AppStyles.secondaryButton, { marginBottom: 8 }]}
                  onPress={() => setShowForgotPassword(false)}
                >
                  <Text style={AppStyles.secondaryButtonText}>Back to Sign In</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        ) : (
          <>
            <TextInput
              placeholder="Email"
              value={username}
              onChangeText={setUsername}
              autoCapitalize="none"
              style={[AppStyles.textInput, { marginBottom: 12 }]}
              placeholderTextColor={placeholderTextColor}
            />
            <TextInput
              placeholder="Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              style={[AppStyles.textInput, { marginBottom: 12 }]}
              placeholderTextColor={placeholderTextColor}
            />
            {Platform.OS === 'web' && (
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                <TouchableOpacity
                  style={{
                    width: 20,
                    height: 20,
                    borderWidth: 2,
                    borderColor: rememberMe ? '#007AFF' : '#ccc',
                    backgroundColor: rememberMe ? '#007AFF' : 'transparent',
                    borderRadius: 3,
                    marginRight: 8,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onPress={() => setRememberMe(!rememberMe)}
                >
                  {rememberMe && (
                    <Text style={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}>✓</Text>
                  )}
                </TouchableOpacity>
                <Text style={{ color: '#666', fontSize: 14 }}>Remember me</Text>
              </View>
            )}
            <TouchableOpacity
              style={[AppStyles.primaryButton, { marginBottom: 8 }]}
              onPress={handleSignIn}
            >
              <Text style={AppStyles.primaryButtonText}>Sign In</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[AppStyles.secondaryButton, { marginBottom: 8 }]}
              onPress={() => setShowSignUp(true)}
            >
              <Text style={AppStyles.secondaryButtonText}>Register</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[AppStyles.linkButton, { marginBottom: 8 }]}
              onPress={() => setShowForgotPassword(true)}
            >
              <ThemedText style={AppStyles.linkButtonText}>Forgot Password?</ThemedText>
            </TouchableOpacity>
          </>
        )}
        {error && <ThemedText style={AppStyles.errorText}>{error}</ThemedText>}
      </View>
    </ThemedView>
  );
}

export function SignOutForm({ onError = () => {}, onSignOut }: { onError?: (error: string) => void, onSignOut?: () => void }) {
  const { isLoading, isAuthenticated, setIsAuthenticated } = useAuth();
  const clearAllData = useCampaignStore(state => state.clearAllData);
  const clearNavigationData = useNavigationStore(state => state.clearAllData);
  const clearLeafletingData = useLeafletingStore(state => state.clearAllData);

  const handleSignOut = async () => {
    try {
      await signOut();
      // Clear all data from stores
      clearAllData();
      clearNavigationData();
      clearLeafletingData();
      // Clear the stored username and reset to default configuration
      await switchConfiguration(null);
      setIsAuthenticated(false);
      
      // Clear user context in Sentry
      if (!__DEV__) {
        Sentry.setUser(null);
      }
      
      onSignOut?.();
    } catch (error) {
      console.error('[SignOutForm] Sign out error:', error);
      // Even if signOut fails, clear stores, configuration and state
      clearAllData();
      clearNavigationData();
      clearLeafletingData();
      await switchConfiguration(null);
      setIsAuthenticated(false);
      
      // Clear user context in Sentry
      if (!__DEV__) {
        Sentry.setUser(null);
      }
      
      onSignOut?.();
      onError(error instanceof Error ? error.message : 'Failed to sign out');
    }
  };

  if (isLoading) {
    return (
      <View style={AppStyles.centeredContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (isAuthenticated) {
    return (
      <View style={{ alignItems: 'flex-end', padding: 10 }}>
        <TouchableOpacity
          style={[AppStyles.secondaryButton, { maxWidth: 400, width: '100%'}]}
          onPress={handleSignOut}
        >
          <Text style={AppStyles.secondaryButtonText}>Sign Out</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return null;
} 